import { readBlob, type OfflineDb } from './db'
import { backoffMs, classifyResponse } from './conflicts'
import { getOp, removeOp, runnableOps } from './outbox'
import { sha256Base64 } from './crypto'
import type { OpOutcome, OutboxOp } from './types'

/**
 * The sync engine (doc §5): drain the per-user outbox in enqueue order,
 * respecting dependencies (attachments before their parent), replaying each
 * op against the NORMAL stateless endpoint with its Idempotency-Key. One
 * stuck op never blocks independent ones; failures back off exponentially;
 * a 401 pauses the drain for sync-time re-auth (§9) and every queued op is
 * preserved for the same user.
 */

export interface SyncCallbacks {
  /** The authenticated fetch (attaches the bearer). */
  fetcher: (path: string, init: RequestInit) => Promise<Response>
  /** 401 mid-drain: try to re-auth (refresh). Return false to stop —
   * ops stay queued until the same user signs in again. */
  onAuthRequired?: () => Promise<boolean>
  /** An op applied on the server — reconcile local state (real numbers,
   * server-computed fields), then invalidate caches. */
  onOpApplied?: (op: OutboxOp, body: unknown) => void | Promise<void>
  /** An op entered conflict/failed — the sync center should surface it. */
  onOpStuck?: (op: OutboxOp) => void
  /** Any outbox mutation (badge/count refresh). */
  onChange?: () => void
}

export interface DrainResult {
  applied: number
  remaining: number
  authFailed: boolean
}

/**
 * Give up on an op after this many retryable failures (audit RUN-6). Without a
 * cap, an undeliverable op retries every 5 minutes forever: it holds
 * `pendingCount` above zero, keeps the periodic drain firing, and drains the
 * device battery with no way for the worker to see or clear it. Moving it to
 * `failed` hands it to the sync center's existing Retry/Discard controls.
 */
const MAX_ATTEMPTS = 12
/**
 * FF-AUDIT-069: a 5xx that carries a FloorFile error envelope means the API
 * ran and failed on THIS body. Twelve retries with five-minute backoff kept
 * such an op `QUEUED` for an hour while the person believed it had landed.
 * Three attempts cover a transient database hiccup; after that the op is
 * dead-lettered to the sync center with Retry / Discard.
 */
const APP_ERROR_MAX_ATTEMPTS = 3

export class SyncEngine {
  private db: OfflineDb
  private cb: SyncCallbacks
  private draining = false

  constructor(db: OfflineDb, callbacks: SyncCallbacks) {
    this.db = db
    this.cb = callbacks
  }

  get syncing(): boolean {
    return this.draining
  }

  /**
   * Recover ops stranded mid-flight by a tab close, crash, or OS kill (audit
   * RUN-5). `syncing` is written before the network call and nothing ever reset
   * it, so such rows became invisible to `runnableOps` FOREVER — and the sync
   * center only offers Retry/Discard for `failed`, so there was no manual
   * escape either. That is silent data loss, and it also deadlocks any child op
   * waiting on the stranded one. Call once per user before the first drain.
   */
  async recoverStranded(userId: string): Promise<number> {
    const stuck = await this.db.outbox.where('userId').equals(userId).toArray()
    let recovered = 0
    for (const op of stuck) {
      if (op.status !== 'syncing') continue
      // Re-queue immediately: the server is idempotent by Idempotency-Key and
      // by client-minted id, so a replay of an op that DID land is a safe no-op.
      //
      // A FUTURE `nextAttemptAt` is PRESERVED, not flattened to 0 (audit
      // FF-12). Zeroing it unconditionally discards a backoff some other branch
      // deliberately set, which is how a vault-locked op ended up retrying on
      // every drain. A genuinely stranded op has no future schedule, so it
      // still starts immediately — the common case is unchanged.
      await this.db.outbox.update(op.opId, {
        status: 'pending',
        nextAttemptAt: op.nextAttemptAt > Date.now() ? op.nextAttemptAt : 0,
      })
      recovered += 1
    }
    if (recovered > 0) this.cb.onChange?.()
    return recovered
  }

  /**
   * Cross-tab mutual exclusion (audit RUN-16). `draining` is per-INSTANCE, and
   * the web app constructs one runtime per tab over the SAME IndexedDB — so two
   * tabs could drain the same outbox concurrently, with the `syncing` flag
   * acting only as a TOCTOU guard. Web Locks makes the drain single-writer
   * across tabs; `ifAvailable` means a second tab skips rather than queues.
   * Falls through unguarded where the API is unavailable (older WebViews).
   */
  private async withDrainLock<T>(fallback: T, run: () => Promise<T>): Promise<T> {
    const locks = (globalThis as { navigator?: { locks?: LockManager } }).navigator?.locks
    if (!locks) return run()
    const result = await locks.request('floorfile-outbox-drain', { ifAvailable: true }, async (lock) =>
      lock ? run() : fallback,
    )
    return result as T
  }

  async drain(userId: string): Promise<DrainResult> {
    if (this.draining) return { applied: 0, remaining: await this.db.outbox.where('userId').equals(userId).count(), authFailed: false }
    return this.withDrainLock({ applied: 0, remaining: await this.remaining(userId), authFailed: false }, () =>
      this.drainLocked(userId),
    )
  }

  private async drainLocked(userId: string): Promise<DrainResult> {
    this.draining = true
    // Safe here: the Web Lock guarantees no other tab is mid-flight, so any
    // `syncing` row is genuinely abandoned rather than actively in progress.
    await this.recoverStranded(userId)
    let applied = 0
    let authFailed = false
    try {
      // Loop until a full pass applies nothing (deps unlock as parents land).
      for (;;) {
        const batch = await runnableOps(this.db, userId)
        if (batch.length === 0) break
        let progressed = false
        for (const op of batch) {
          await this.db.outbox.update(op.opId, { status: 'syncing' })
          this.cb.onChange?.()
          const outcome = await this.execute(op)
          if (outcome.type === 'applied') {
            await removeOp(this.db, op.opId)
            if (op.kind === 'attachment-upload') await this.db.blobs.delete(op.entityId)
            // Audit RUN-20: unguarded, a consumer callback that threw aborted
            // the REST of the batch — ops that had already been applied
            // server-side stayed queued and replayed. The callback is a
            // notification, never part of the operation's success.
            try {
              await this.cb.onOpApplied?.(op, outcome.body)
            } catch (err) {
              console.error('[offline] onOpApplied threw', err)
            }
            applied += 1
            progressed = true
          } else if (outcome.type === 'auth') {
            const recovered = (await this.cb.onAuthRequired?.()) ?? false
            // Count the attempt and apply backoff (audit RUN-20). Re-queuing
            // with an untouched `nextAttemptAt` while reporting progress spins
            // this loop with zero delay whenever re-auth "succeeds" but the op
            // keeps 401ing.
            const attempts = op.attempts + 1
            await this.db.outbox.update(op.opId, {
              status: 'pending',
              attempts,
              ...(recovered ? {} : { nextAttemptAt: Date.now() + backoffMs(attempts) }),
            })
            if (!recovered || attempts >= MAX_ATTEMPTS) {
              authFailed = true
              this.cb.onChange?.()
              return { applied, remaining: await this.remaining(userId), authFailed }
            }
            progressed = true // token refreshed; the op reruns next pass
          } else if (outcome.type === 'retryable') {
            const attempts = op.attempts + 1
            // A locked entry is not a failing op — it is waiting for the user
            // to unlock the vault (RUN-1). Never let it burn attempts toward
            // the dead-letter cap, or a locked device would eventually discard
            // perfectly good captured work.
            if (op.locked) {
              // `status: 'pending'` is load-bearing (audit FF-12). Left at
              // 'syncing' — which line 119 set — this row is re-queued by
              // `recoverStranded` on the very next drain, and that resets
              // `nextAttemptAt` to 0, discarding the backoff set right here. On
              // a locked device with a large captured outbox every locked op
              // then retried on every drain with no delay, writing to
              // IndexedDB and firing `onChange` each time: the battery drain
              // MAX_ATTEMPTS exists to prevent, arriving by another route.
              await this.db.outbox.update(op.opId, {
                status: 'pending',
                nextAttemptAt: Date.now() + backoffMs(1),
                lastError: outcome.error,
              })
            } else if (attempts >= (outcome.appError ? APP_ERROR_MAX_ATTEMPTS : MAX_ATTEMPTS)) {
              // Dead-letter (audit RUN-6): stop retrying forever and surface it
              // to the sync center, where the worker can retry or discard.
              await this.db.outbox.update(op.opId, {
                status: 'failed',
                attempts,
                lastError: `Gave up after ${attempts} attempts: ${outcome.error}`,
              })
              const stuck = await getOp(this.db, op.opId)
              if (stuck) this.cb.onOpStuck?.(stuck)
            } else {
              await this.db.outbox.update(op.opId, {
                status: 'pending',
                attempts,
                nextAttemptAt: Date.now() + backoffMs(attempts),
                lastError: outcome.error,
              })
            }
          } else if (outcome.type === 'conflict') {
            await this.db.outbox.update(op.opId, {
              status: 'conflict',
              conflict: {
                code: outcome.code,
                message: outcome.message,
                ...(outcome.serverState !== undefined ? { serverState: outcome.serverState } : {}),
              },
            })
            const stuck = await getOp(this.db, op.opId)
            if (stuck) this.cb.onOpStuck?.(stuck)
          } else {
            await this.db.outbox.update(op.opId, {
              status: 'failed',
              lastError: `${outcome.code}: ${outcome.message}`,
            })
            const stuck = await getOp(this.db, op.opId)
            if (stuck) this.cb.onOpStuck?.(stuck)
          }
          this.cb.onChange?.()
        }
        if (!progressed) break
      }
      return { applied, remaining: await this.remaining(userId), authFailed }
    } finally {
      this.draining = false
      this.cb.onChange?.()
    }
  }

  private async remaining(userId: string): Promise<number> {
    return this.db.outbox.where('userId').equals(userId).count()
  }

  private async execute(op: OutboxOp): Promise<OpOutcome> {
    try {
      if (op.kind === 'attachment-upload') return await this.executeUpload(op)
      // Defence in depth behind runnableOps' `locked` filter (audit RUN-1):
      // an op whose body did not survive decoding must NEVER be sent. Omitting
      // the body would either lose the work on a 4xx or apply the transition
      // with server defaults. Retryable, not permanent — the content is intact
      // on disk and decodes once the vault is unlocked.
      // NB: keyed on `locked`, not on a missing payload — DELETE and other
      // bodyless ops legitimately carry no payload and must still drain.
      if (op.locked) {
        return { type: 'retryable', error: 'Encrypted entry is locked — unlock to sync.' }
      }
      const res = await this.cb.fetcher(op.endpoint, {
        method: op.method,
        headers: {
          'content-type': 'application/json',
          'idempotency-key': op.idempotencyKey,
          // Mutable edits replay against the version they were made from
          // (§7) — the server's If-Match boundary turns a moved record into
          // a classified STALE_UPDATE instead of a silent last-write-wins.
          ...(op.baseVersion !== undefined ? { 'if-match': `"${op.baseVersion}"` } : {}),
        },
        ...(op.payload !== undefined ? { body: JSON.stringify(op.payload) } : {}),
      })
      const body: unknown = await res.json().catch(() => undefined)
      return classifyResponse(res.status, body, res.headers.get('x-idempotent-replay'))
    } catch (err) {
      return { type: 'retryable', error: err instanceof Error ? err.message : 'network error' }
    }
  }

  /** The 3-step attachment protocol (doc §6) with the client-minted id. */
  private async executeUpload(op: OutboxOp): Promise<OpOutcome> {
    const meta = op.attachment
    if (!meta) return { type: 'permanent', code: 'MALFORMED_OP', message: 'Upload op without metadata.' }
    const blob = await readBlob(this.db, op.entityId)
    if (!blob) return { type: 'permanent', code: 'BLOB_MISSING', message: 'The captured file is no longer on this device.' }

    // Audit FF-08: hash once, reuse the same buffer for the PUT, so the header
    // cannot disagree with what the server signed. A replayed op re-registers
    // with the SAME id and the same bytes, so it produces the same digest and
    // the idempotent-by-id path is unaffected.
    const bytes = await blob.arrayBuffer()
    const checksumSha256 = await sha256Base64(bytes)

    const register = await this.cb.fetcher('/api/v1/attachments', {
      method: 'POST',
      headers: { 'content-type': 'application/json', 'idempotency-key': op.idempotencyKey },
      body: JSON.stringify({
        id: op.entityId,
        entityType: meta.targetEntityType,
        entityId: meta.targetEntityId,
        module: meta.module,
        fileName: meta.fileName,
        mimeType: meta.mimeType,
        sizeBytes: blob.size,
        ...(checksumSha256 ? { checksumSha256 } : {}),
        ...(meta.context ? { context: meta.context } : {}),
        ...(meta.derivedFromAttachmentId ? { derivedFromAttachmentId: meta.derivedFromAttachmentId } : {}),
      }),
    })
    const registerBody: unknown = await register.json().catch(() => undefined)
    if (!register.ok) return classifyResponse(register.status, registerBody, null)
    const uploadUrl = (registerBody as { data?: { uploadUrl?: string } })?.data?.uploadUrl
    if (!uploadUrl) return { type: 'retryable', error: 'uploadUrl missing' }

    // An absolute URL is a presigned R2 PUT — it must go out as a bare fetch;
    // the auth-attaching fetcher would send an Authorization header that R2
    // rejects alongside the query-string signature. A relative path is the
    // Worker-proxied route and needs the fetcher's bearer token.
    const putFetch = /^https?:\/\//i.test(uploadUrl) ? fetch : this.cb.fetcher
    const put = await putFetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'content-type': 'application/octet-stream',
        // MUST be byte-identical to what the server signed (audit FF-08).
        ...(checksumSha256 ? { 'x-amz-checksum-sha256': checksumSha256 } : {}),
      },
      body: bytes,
    })
    // A 409 here means a prior half-completed attempt already stored the
    // bytes — confirm below independently verifies the object landed.
    if (!put.ok && put.status !== 409) {
      return classifyResponse(put.status, await put.json().catch(() => undefined), null)
    }

    const confirm = await this.cb.fetcher(`/api/v1/attachments/${op.entityId}/confirm`, { method: 'POST' })
    const confirmBody: unknown = await confirm.json().catch(() => undefined)
    return classifyResponse(confirm.status, confirmBody, null)
  }
}
